import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LeadForm from "@/components/LeadForm";
import SeoHead from "@/components/SeoHead";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";

const DEVICE_IMG = "https://cdn.prod.website-files.com/648a1891f44afca175d3b615/648e3494052fafd80ac65895_edgeOne%20Front_lg.webp";

export default function EdgeOnePage() {
  const { t } = useLang();
  const e = t.edgeOnePage;

  return (
    <div className="min-h-screen bg-white">
      <SeoHead path="/products/edgeone" title={e.metaTitle} description={e.metaDescription} />
      <Navbar />
      {/* hero */}
      <section className="pt-32 pb-16 bg-slate-50 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-blue-600 mb-8" data-testid="back-home">
            <ArrowLeft size={16} /> {e.back}
          </Link>
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] font-mono-tech text-blue-600 mb-4">{e.eyebrow}</div>
              <h1 className="font-display font-black text-5xl md:text-7xl tracking-tight">{e.h1}</h1>
              <p className="mt-6 text-xl text-slate-600 leading-relaxed">{e.sub}</p>
              <p className="mt-4 text-base text-slate-600 leading-relaxed max-w-2xl">{e.intro}</p>
              <div className="flex flex-wrap gap-2 mt-6">
                {e.badges.map((b) => (
                  <span key={b} className="text-xs border border-slate-300 px-3 py-1.5 font-mono-tech uppercase tracking-widest text-slate-700">{b}</span>
                ))}
              </div>
              <a href="#quote" className="inline-flex items-center gap-2 mt-8 bg-blue-600 text-white px-6 py-3 font-semibold hover:bg-blue-700 transition-colors" data-testid="edgeone-cta-quote">
                {e.ctaQuote}
              </a>
            </div>
            <div className="bg-white border border-slate-200 p-10 flex items-center justify-center">
              <img
                src={DEVICE_IMG}
                alt="RuggedEdge EdgeOne — ATEX 5G Edge Gateway"
                className="max-h-[420px] object-contain"
                loading="lazy"
                decoding="async"
                width="600"
                height="600"
              />
            </div>
          </div>
        </div>
      </section>

      {/* spec tables */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <h2 className="font-display font-black text-4xl tracking-tight mb-12">{e.specsHeading}</h2>
          <div className="grid md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
            {e.fullSpecs.map((g) => (
              <div key={g.group} className="bg-white p-8">
                <div className="text-xs uppercase tracking-widest font-mono-tech text-blue-600 mb-4">{g.group}</div>
                <table className="w-full text-sm">
                  <tbody>
                    {g.items.map(([k, v]) => (
                      <tr key={k} className="border-b border-slate-100 last:border-0">
                        <td className="py-3 pr-4 text-slate-500 font-mono-tech text-xs uppercase tracking-widest align-top w-1/3">{k}</td>
                        <td className="py-3 text-slate-900 font-medium">{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ecosystem */}
      <section className="py-20 bg-slate-50 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <h2 className="font-display font-black text-4xl tracking-tight mb-8">{e.ecosystemHeading}</h2>
          <p className="text-slate-600 max-w-2xl mb-10">{e.ecosystemIntro}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
            {e.peripherals.map((p) => (
              <div key={p} className="bg-white p-5 text-sm font-medium text-slate-800">{p}</div>
            ))}
          </div>
        </div>
      </section>

      {/* quote */}
      <section id="quote" className="py-24 bg-[#0B1121] text-white">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight mb-4">{e.quoteHeading}</h2>
          <p className="text-slate-300 mb-10">{e.quoteIntro}</p>
          <LeadForm variant="dark" source="edgeone-page" productInterest="EdgeOne" />
        </div>
      </section>

      <Footer />
    </div>
  );
}
